import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { TicketInfoInterface } from '../models/ticket-info.interface';
import { ChatMessageInterface } from '../models/chat-message.interface';
import { MultiChatService } from '../../../../shared/services/multi-chat.service';
import { renderMathInDocument } from 'mathlive';
import { RateModalComponent } from '../../chat-components/modals/rate-modal/rate-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ClickhouseService } from '../../../../shared/services/clickhouse.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AppStateInterface } from '../../../../store/state/app.state';
import { combineLatest, Observable, Subject } from 'rxjs';
import {
  hasSubscriptionSelector,
  userBalanceSelector,
} from '../../../../store/selectors/billing.selectors';
import { isFreeUserRegisteredSelector } from '../../../../store/selectors/event-variables.selectors';
import { FormControl } from '@angular/forms';
import { AppModalService } from '../../../../shared/services/app-modal.service';
import { setExpertName } from '../../../../store/actions/expert.actions';

@Component({
  selector: 'app-multi-chat-display',
  templateUrl: './multi-chat-display.component.html',
  styleUrls: ['./multi-chat-display.component.scss'],
})
export class MultiChatDisplayComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() chosenTicket!: TicketInfoInterface;
  @Input() isExpertTyping!: boolean;
  @Input() isTicketHasNewMessage!: boolean;
  @Input() onMessagesRead$!: Subject<{
    messages: Array<{ author: string; messageId: number; status: string }>;
  }>;
  @Output() unsolvedApproved: EventEmitter<{
    ticket_id: number;
  }> = new EventEmitter<{
    ticket_id: number;
  }>();
  @Output() questionAsked: EventEmitter<{
    ticket_id: number;
  }> = new EventEmitter<{
    ticket_id: number;
  }>();
  @Output() rateSet: EventEmitter<{ ticket_id: number }> = new EventEmitter<{
    ticket_id: number;
  }>();
  @Output() newTaskCreated: EventEmitter<any> = new EventEmitter<any>();

  messages: Array<ChatMessageInterface> = [];
  repliedMessage: any = null; // ChatMessageInterface
  currentExpert: any = {};
  elementShow = {
    scrollBottomBtn: {
      show: false,
      msgIndicatorShow: false,
    },
    markSolvedMessageShow: false,
    chatFooterShow: false,
    showMoreBtnShow: false,
  };

  messageControl: FormControl;

  fileControl: FormControl;

  billingInfo = {
    balance: 0,
    hasSubscription: false,
    isUserFree: false,
  };

  @ViewChild('scrollMe') private myScrollContainer!: ElementRef;

  private scrollContainer: HTMLDivElement;

  constructor(
    private multiChatService: MultiChatService,
    private modalService: NgbModal,
    private clickHouse: ClickhouseService,
    private spinner: NgxSpinnerService,
    private store: Store<AppStateInterface>,
    private myModal: AppModalService,
  ) {
    this.scrollContainer = document.createElement('div');
    this.messageControl = new FormControl();
    this.fileControl = new FormControl();
    combineLatest([
      this.store.select(hasSubscriptionSelector),
      this.store.select(userBalanceSelector),
      this.store.select(isFreeUserRegisteredSelector),
    ]).subscribe(([hasSubscription, userBalance, isFreeUserRegistered]) => {
      this.billingInfo.balance = userBalance;
      this.billingInfo.hasSubscription = hasSubscription;
      this.billingInfo.isUserFree = isFreeUserRegistered;
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.chosenTicket?.currentValue) {
      this.changeTicket(changes.chosenTicket.currentValue, changes.chosenTicket.previousValue);
    }
    if (changes.isExpertTyping?.currentValue) {
      this.isExpertTyping = changes.isExpertTyping.currentValue;
    }
    if (changes.isTicketHasNewMessage?.currentValue) {
      this.uploadNewMessage();
    }
  }

  ngOnInit(): void {
    this.onMessagesRead$.subscribe((resp) => {
      resp.messages.forEach((message) => {
        if (this.messages.find((m) => m.id == message.messageId)) {
          this.messages.find((m) => m.id == message.messageId)!.status = message.status;
        }
      });
    });
  }

  ngAfterViewInit() {
    this.scrollContainer = this.myScrollContainer.nativeElement;
  }

  isFormula(msg: ChatMessageInterface) {
    return !!((!msg?.file?.name && msg?.file?.url) || msg?.type === 'formula');
  }

  askQuestionClick() {
    import(
      '../../chat-components/modals/ask-question-modal/ask-question-modal-component.module'
    ).then(({ AskQuestionModalComponentModule }) => {
      const AskQuestionModalComponent = AskQuestionModalComponentModule.getComponent();
      const modal = this.modalService.open(AskQuestionModalComponent, {
        backdrop: 'static',
        keyboard: false,
      });
      modal.componentInstance.questionAsked.subscribe((text: string) => {
        this.sendAskQuestion(text);
        this.clickHouse.storeEvent('ask_a_question_send_click').subscribe(() => {});
      });
    });
  }

  sendAskQuestion(text: string) {
    this.clickHouse.storeEvent('ask_a_question_click').subscribe(() => {});
    this.spinner.show();
    this.multiChatService
      .askQuestion({ ticket_id: this.chosenTicket?.id, text })
      .subscribe((response: any) => {
        this.spinner.hide();
        if (response.status === 'success') {
          this.questionAsked.emit({ ticket_id: this.chosenTicket.id });
        }
      });
  }

  markAsDone() {
    const lastSolution = [...this.messages]
      .reverse()
      .find((el: ChatMessageInterface) => el.author.is_expert === 'true');
    this.clickHouse.storeEvent('mark_as_done_click').subscribe(() => {});
    const modal = this.modalService.open(RateModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });
    modal.closed.subscribe(() => {
      this.rateSet.emit({ ticket_id: this.chosenTicket.id });
    });
    modal.componentInstance.expert_id = lastSolution!.expert_id;
    modal.componentInstance.ticket_id = this.chosenTicket?.id;
  }

  onReply(message: ChatMessageInterface) {
    this.repliedMessage = message;
    if (this.isFormula(message) || message.type === 'autosolution') {
      setTimeout(() => {
        renderMathInDocument({
          TeX: {
            delimiters: {
              inline: [
                ['$$', '$$'],
                ['$', '$'],
              ],
              display: [['$$', '$$']],
            },
          },
        });
      }, 10);
    }
  }

  loadHistoryNextPage() {
    this.elementShow.showMoreBtnShow = false;
    this.spinner.show('loadMoreSpinner');
    combineLatest([
      this.multiChatService.getMessagesFromTicket(this.chosenTicket?.id, this.messages[0].id),
      new Observable((subscriber) => {
        // Observable for pretty btn animation
        setTimeout(() => {
          subscriber.next();
        }, 1000);
      }),
    ]).subscribe(([resp]: any) => {
      resp.data.comments
        .sort((a: any, b: any) => b.id - a.id)
        .forEach((message: any) => {
          if (!this.messages.find((msg) => msg.id === message.id)) {
            this.messages.unshift(message);
          }
        });
      this.elementShow.showMoreBtnShow = resp.data.show_more;
      this.spinner.hide('loadMoreSpinner');
    });
  }

  trackByFn(_index: any, item: ChatMessageInterface) {
    return item.id;
  }

  scrollMessageContainer() {
    this.elementShow.scrollBottomBtn.show =
      Math.floor(this.scrollContainer.scrollHeight - this.scrollContainer.clientHeight) >
      Math.floor(this.scrollContainer.scrollTop);
  }

  scrollToBottom(): void {
    this.scrollContainer.scroll({
      top: this.scrollContainer.scrollHeight,
      left: 0,
      behavior: 'smooth',
    });
    this.elementShow.scrollBottomBtn.msgIndicatorShow = false;
  }

  sendTextMessage(event?: any) {
    event?.preventDefault();
    if (this.messageControl.value?.trim()) {
      const message = this.messageControl.value?.trim();
      this.messageControl.setValue(null);
      this.repliedMessage = null;
      this.multiChatService
        .sendTextMessageToTicket(message, this.repliedMessage?.id, undefined, this.chosenTicket.id)
        .subscribe((resp: any) => {
          if (resp.status === 'success') {
          }
        });
    }
  }

  sendFileMessage(event: any) {
    this.multiChatService
      .sendFileMessageToTicket(
        event.target.files[0],
        this.repliedMessage?.id,
        undefined,
        this.chosenTicket.id,
      )
      .subscribe((resp: any) => {
        if (resp.status === 'success') {
        }
        event.target.value = '';
        this.repliedMessage = null;
      });
  }

  billingBtnClick(event: any) {
    event.preventDefault();
    if (this.billingInfo.hasSubscription) {
      import('../../../../components/modals/billing-modal/billing-modal-component.module').then(
        ({ BillingModalComponentModule }) => {
          const BillingModalComponent = BillingModalComponentModule.getComponent();
          this.modalService.open(BillingModalComponent, {
            backdrop: 'static',
            keyboard: false,
          });
        },
      );
    } else {
      import('../../../../components/modals/plan-modal/plan-modal-component.module').then(
        ({ PlanModalComponentModule }) => {
          const PlanModalComponent = PlanModalComponentModule.getComponent();
          this.modalService.open(PlanModalComponent, {
            backdrop: 'static',
            keyboard: false,
          });
        },
      );
    }
  }

  createNewTask() {
    this.newTaskCreated.emit();
  }

  ngOnDestroy() {
    this.store.dispatch(setExpertName({ name: '' }));
  }

  private showUnsolvedMessage() {
    this.myModal.open({
      title:
        'Do you still want the task to be solved in 24 hours? Due to complexity, it will cost you x2 coins.',
      canClose: false,
      buttons: [
        {
          title: 'Solve',
          type: 'yellow',
          outline: true,
          callback: combineLatest([
            this.multiChatService.setTicketStatusByUser('yes', this.chosenTicket.id),
            this.clickHouse.storeEvent('unsolved_popup_solve_click'),
          ]).pipe(
            tap(() => {
              this.unsolvedApproved.emit({ ticket_id: this.chosenTicket.id });
            }),
          ),
        },
        {
          title: 'Decline',
          type: 'gray',
          outline: false,
          callback: combineLatest([
            this.multiChatService.setTicketStatusByUser('no', this.chosenTicket.id),
            this.clickHouse.storeEvent('unsolved_popup_decline_click'),
          ]).pipe(
            tap(() => {
              this.unsolvedApproved.emit({ ticket_id: this.chosenTicket.id });
            }),
          ),
        },
      ],
      initialCallback: () => {
        this.clickHouse.storeEvent('unsolved_popup_shown').subscribe(() => {});
      },
    });
  }

  setText(html: any) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newHtml = doc.documentElement.textContent;
    return newHtml;
  }

  private getTicketMessages(ticket: TicketInfoInterface) {
    return this.multiChatService.getMessagesFromTicket(ticket.id).pipe(
      tap((response) => {
        if (response.status === 'success') {
          response.data.comments
            .sort((a: any, b: any) => a.created_at - b.created_at)
            .forEach((message: any) => {
              const availableMessage = this.messages.find((msg) => msg.id === message.id);

              if (message.type === 'autosolution' || message.type === 'markdown') {
                message.body = this.setText(message.body);
              }

              if (availableMessage) {
                const availableMessageIndex = this.messages.findIndex(
                  (msg) => msg.id === message.id,
                );
                this.messages[availableMessageIndex] = message;
              } else {
                this.messages.push(message);
              }
            });
          if (
            Math.floor(this.scrollContainer.scrollHeight - this.scrollContainer.clientHeight) > 150
          ) {
            this.scrollToBottom();
          } else {
            this.elementShow.scrollBottomBtn.msgIndicatorShow = true;
          }
        }
      }),
    );
  }

  private reInitialise() {
    this.messages = [];
    this.repliedMessage = undefined;
    this.currentExpert = {};

    this.elementShow = {
      scrollBottomBtn: {
        show: false,
        msgIndicatorShow: false,
      },
      markSolvedMessageShow: false,
      chatFooterShow: false,
      showMoreBtnShow: false,
    };
  }

  private uploadNewMessage() {
    this.getTicketMessages(this.chosenTicket).subscribe(() => {});
  }

  private changeTicket(newTicket: TicketInfoInterface, previousTicket?: TicketInfoInterface) {
    if (newTicket.id !== previousTicket?.id) {
      this.reInitialise();
      this.spinner.show();
      this.getTicketMessages(newTicket).subscribe((response) => {
        this.spinner.hide();
        this.elementShow.showMoreBtnShow = response.data.show_more;
        this.currentExpert = [...this.messages]
          .reverse()
          .find((el: any) => el.author.is_expert === 'true')?.author || { name: 'Improvemath' };
        this.store.dispatch(
          setExpertName({
            name:
              [...this.messages].reverse().find((el: any) => el.author.is_expert === 'true')?.author
                .name || 'Improvemath',
          }),
        );
      });
    }
    if (newTicket.need_confirm_close_unsolved_ticket) {
      this.showUnsolvedMessage();
    }
    this.elementShow.markSolvedMessageShow =
      newTicket.status === 'closed' &&
      newTicket.is_charged_solution &&
      newTicket.need_confirm_close_ticket;
    this.elementShow.chatFooterShow =
      (newTicket.status !== 'closed' &&
        !(newTicket.need_confirm_close_ticket || newTicket.need_confirm_close_unsolved_ticket)) ||
      newTicket.fake_close;
  }
}
